import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBranding } from '@/contexts/BrandingContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { X, Building2, HelpCircle } from 'lucide-react';

/**
 * PropertyPopup UX State Management
 * Only stores non-sensitive UI state: dismiss timestamp and session flag.
 * No auth tokens, user data, or PII is stored in localStorage/sessionStorage.
 */
const DISMISS_KEY = 'estadia_popup_dismissed';
const DISMISS_DAYS = 7;
const SESSION_SHOWN_KEY = 'estadia_popup_shown_session';

const ADMIN_ROUTES = ['/admin', '/dashboard', '/reservations', '/calendar', '/settings', '/users', '/reports', '/verification', '/notifications', '/qr', '/gestion', '/mis-reservas', '/mi-cuenta', '/proximos-viajes', '/checkin', '/pago-bancard', '/payment-success'];

function wasDismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return (Date.now() - Number(ts)) / (1000 * 60 * 60 * 24) < DISMISS_DAYS;
  } catch { return false; }
}

function wasShownThisSession() {
  try { return sessionStorage.getItem(SESSION_SHOWN_KEY) === '1'; }
  catch { return false; }
}

function markShownThisSession() {
  try { sessionStorage.setItem(SESSION_SHOWN_KEY, '1'); } catch { /* non-critical */ }
}

export default function PropertyPopup() {
  const { branding } = useBranding();
  const { t } = useI18n();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const hasTriggered = useRef(false);

  const isAdminRoute = ADMIN_ROUTES.some(r => location.pathname.startsWith(r));

  useEffect(() => {
    if (isAdminRoute || hasTriggered.current || wasDismissedRecently() || wasShownThisSession()) {
      return;
    }

    const handleScroll = () => {
      const scrollH = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollH <= 0) return;
      const pct = (window.scrollY / scrollH) * 100;
      if (pct >= 50) {
        hasTriggered.current = true;
        markShownThisSession();
        setShow(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAdminRoute, location.pathname]);

  const dismiss = () => {
    setShow(false);
    hasTriggered.current = true;
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* non-critical */ }
    markShownThisSession();
  };

  if (!show) return null;

  const phone = branding?.social?.whatsapp || branding?.contact?.phone || '+595982188147';
  const cleanPhone = phone.replace(/[^0-9+]/g, '').replace('+', '');
  const waMessage = encodeURIComponent('Hola, quiero sumar mi alojamiento a Estadía 365. Me gustaría recibir información sobre cómo trabajan.');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="property-popup-overlay">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300" data-testid="property-popup">
        <button onClick={dismiss} className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10 transition-colors" data-testid="popup-close">
          <X className="w-4 h-4 text-gray-600" />
        </button>
        <div className="p-6 pb-0">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--brand-accent, #DCE9E2)' }}>
            <Building2 className="w-7 h-7" style={{ color: 'var(--brand-primary, #264639)' }} />
          </div>
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }}>
            {t('popup.title')}
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>
            {t('popup.desc')}
          </p>
        </div>
        <div className="px-6 py-4">
          <p className="text-xs mb-4" style={{ color: 'var(--brand-text-muted, #78716C)' }}>
            {t('popup.types')}
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/sumar-alojamiento" onClick={dismiss}>
              <Button className="w-full text-white" style={{ backgroundColor: 'var(--brand-primary, #264639)' }} data-testid="popup-cta-main">
                <HelpCircle className="w-4 h-4 mr-2" />
                {t('popup.faq')}
              </Button>
            </Link>
            <a href={`https://wa.me/${cleanPhone}?text=${waMessage}`} target="_blank" rel="noopener noreferrer" onClick={dismiss}>
              <Button variant="outline" className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/5" data-testid="popup-cta-wa">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#25D366] mr-2 flex-shrink-0">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {t('popup.whatsapp')}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
